import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

function getWeekRange(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const mon = new Date(d); mon.setDate(d.getDate() + diffToMon);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { start: mon.toISOString().split('T')[0], end: sun.toISOString().split('T')[0] };
}

function formatDisplay(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function AttendanceReports() {
  const { attendance, students, classes } = useApp();
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';
  const availableClasses = isTeacher
    ? classes.filter(c => c.classTeacherId === currentUser?.profileId)
    : classes;

  const [period, setPeriod] = useState('month'); // day | week | month
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedStudent, setExpandedStudent] = useState(null);

  // Derive selected month/week/day label
  const periodLabel = useMemo(() => {
    if (period === 'day') return formatDisplay(selectedDate);
    if (period === 'week') {
      const { start, end } = getWeekRange(selectedDate);
      return `${formatDisplay(start)} – ${formatDisplay(end)}`;
    }
    // month
    return new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, [period, selectedDate]);

  // Filter attendance records for the selected period
  const filteredAttendance = useMemo(() => {
    return attendance.filter(a => {
      if (a.entityType !== 'student') return false;
      const d = a.date;
      if (period === 'day') return d === selectedDate;
      if (period === 'week') {
        const { start, end } = getWeekRange(selectedDate);
        return d >= start && d <= end;
      }
      // month
      return d.slice(0, 7) === selectedDate.slice(0, 7);
    });
  }, [attendance, period, selectedDate]);

  // Build student rows
  const studentRows = useMemo(() => {
    const filtered = students.filter(s =>
      s.status === 'active' &&
      (selectedClass === 'all' || s.classId === selectedClass) &&
      availableClasses.some(c => c.id === s.classId)
    ).sort((a, b) => {
      if (a.classId !== b.classId) return a.classId.localeCompare(b.classId);
      return Number(a.rollNumber) - Number(b.rollNumber);
    });

    return filtered.map(s => {
      const records = filteredAttendance.filter(a => a.entityId === s.id);
      const present = records.filter(a => a.status === 'present').length;
      const absent = records.filter(a => a.status === 'absent').length;
      const late = records.filter(a => a.status === 'late').length;
      const total = records.length;
      const pct = total > 0 ? Math.round(((present + late) / total) * 100) : null;
      const cls = classes.find(c => c.id === s.classId);
      return { ...s, present, absent, late, total, pct, className: cls ? `${cls.name}-${cls.section}` : '-', records };
    });
  }, [students, selectedClass, filteredAttendance, classes]);

  // Unique dates in view for the detail row
  const datesInView = useMemo(() => {
    const set = new Set(filteredAttendance.map(a => a.date));
    return [...set].sort();
  }, [filteredAttendance]);

  const statusColor = (s) => {
    if (s === 'present') return 'bg-green-500 text-white';
    if (s === 'absent') return 'bg-red-500 text-white';
    if (s === 'late') return 'bg-yellow-400 text-white';
    return 'bg-gray-100';
  };

  const statusLabel = (s) => {
    if (s === 'present') return 'P';
    if (s === 'absent') return 'A';
    if (s === 'late') return 'L';
    return '—';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold">Attendance Reports</h1>
        <div className="flex items-center gap-2">
          {/* Period toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {['day', 'week', 'month'].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-4 py-1 text-sm font-medium capitalize ${period === p ? 'bg-indigo-600 text-white' : 'bg-white hover:bg-gray-50'}`}>
                {p}
              </button>
            ))}
          </div>
          {/* Date picker */}
          <input
            type={period === 'month' ? 'month' : 'date'}
            value={period === 'month' ? selectedDate.slice(0, 7) : selectedDate}
            onChange={e => {
              const val = e.target.value;
              setSelectedDate(period === 'month' ? val + '-01' : val);
            }}
            className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
          className="px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          <option value="all">All Classes</option>
          {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
        </select>
        <span className="text-sm">{periodLabel}</span>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-3">
        {[['P', 'Present', 'bg-green-500'], ['A', 'Absent', 'bg-red-500'], ['L', 'Late', 'bg-yellow-400']].map(([k, l, c]) => (
          <span key={k} className="flex items-center gap-1">
            <span className={`${c} text-white rounded px-1.5 py-0.5 font-bold`}>{k}</span>
            <span className="text-gray-500">{l}</span>
          </span>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200">
              <th className="px-4 py-1.5 text-left font-bold">Student</th>
              <th className="px-4 py-1.5 text-left font-bold">Class</th>
              <th className="px-4 py-1.5 text-center font-bold">Present</th>
              <th className="px-4 py-1.5 text-center font-bold">Absent</th>
              <th className="px-4 py-1.5 text-center font-bold">Late</th>
              <th className="px-4 py-1.5 text-center font-bold">Total Days</th>
              <th className="px-4 py-1.5 text-center font-bold w-40">Attendance %</th>
            </tr>
          </thead>
          <tbody>
            {studentRows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center">No attendance records for this period.</td></tr>
            )}
            {studentRows.map(s => (
              <React.Fragment key={s.id}>
                <tr
                  className="border-b border-gray-100 hover:bg-indigo-50/30 cursor-pointer"
                  onClick={() => setExpandedStudent(expandedStudent === s.id ? null : s.id)}
                >
                  <td className="px-4 py-1.5 font-medium">
                    <span className="flex items-center gap-1.5">
                      <span className={`transition-transform ${expandedStudent === s.id ? 'rotate-90' : ''}`}>▶</span>
                      {s.firstName} {s.lastName}
                    </span>
                  </td>
                  <td className="px-4 py-1.5">{s.className}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-green-600">{s.present}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-red-500">{s.absent}</td>
                  <td className="px-4 py-1.5 text-center font-semibold text-yellow-600">{s.late}</td>
                  <td className="px-4 py-1.5 text-center">{s.total}</td>
                  <td className="px-4 py-1.5">
                    {s.pct === null ? (
                      <span className="text-gray-400 text-center block">—</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${s.pct >= 75 ? 'bg-green-500' : s.pct >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${s.pct}%` }} />
                        </div>
                        <span className={`font-semibold w-9 text-right ${s.pct >= 75 ? 'text-green-600' : s.pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {s.pct}%
                        </span>
                      </div>
                    )}
                  </td>
                </tr>

                {/* Day-wise detail row */}
                {expandedStudent === s.id && datesInView.length > 0 && (
                  <tr className="bg-indigo-50/20 border-b border-gray-100">
                    <td colSpan={7} className="px-6 py-3">
                      <div className="flex flex-wrap gap-2">
                        {datesInView.map(date => {
                          const rec = s.records.find(r => r.date === date);
                          const st = rec?.status || null;
                          return (
                            <div key={date} className="flex flex-col items-center gap-0.5">
                              <span className="">
                                {(() => { const d = new Date(date + 'T00:00:00'); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`; })()}
                              </span>
                              <span className={`font-bold rounded px-2 py-0.5 ${st ? statusColor(st) : 'bg-gray-100'}`}>
                                {st ? statusLabel(st) : '—'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                {expandedStudent === s.id && datesInView.length === 0 && (
                  <tr className="bg-indigo-50/20 border-b border-gray-100">
                    <td colSpan={7} className="px-6 py-3 text-sm">No records marked for this student in this period.</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AttendanceReports;
